import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SiteShell from '@/components/feature/SiteShell';
import Breadcrumb from '@/components/base/Breadcrumb';
import { usePageMeta } from '@/hooks/usePageMeta';
import { newsArticles } from '@/mocks/news';

const CATEGORIES = ['全部', '电影', '电视剧', '综艺', '行业'] as const;
const PAGE_SIZE = 5;

export default function News() {
  const [active, setActive] = useState<(typeof CATEGORIES)[number]>('全部');
  const [page, setPage] = useState(1);

  usePageMeta({
    title: '影视资讯 - 黄牛视频影视内容平台',
    description:
      '黄牛视频影视资讯频道，提供电影、电视剧、综艺的最新动态、定档消息、幕后故事与行业观察。',
    keywords: '影视资讯,电影新闻,电视剧资讯,综艺资讯,黄牛视频,行业观察',
  });

  const filtered = useMemo(
    () =>
      active === '全部'
        ? newsArticles
        : newsArticles.filter((article) => article.category === active),
    [active],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const hot = newsArticles.slice(0, 5);

  useEffect(() => {
    setPage(1);
  }, [active]);

  return (
    <SiteShell>
      <div className="w-full px-4 pb-2 pt-5 md:px-8 md:pt-7">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '影视资讯' }]} />
      </div>

      <section className="w-full px-4 pb-6 pt-3 md:px-8 md:pb-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-900 text-accent-400">
                <i className="ri-newspaper-line text-xl" />
              </span>
              <div>
                <h1 className="font-heading text-2xl font-extrabold tracking-tight text-foreground-950 md:text-3xl">
                  影视资讯
                </h1>
                <p className="mt-0.5 font-label text-[10px] uppercase tracking-[0.22em] text-foreground-400">
                  Newsroom · Huangniu TV
                </p>
              </div>
            </div>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
              定档消息、幕后特辑、行业观察与剧集讨论，每日整理与影视相关的重要动态。
            </p>
          </div>
          <p className="text-xs text-foreground-500">
            共 <span className="font-label font-bold text-foreground-900">{filtered.length}</span> 篇报道
          </p>
        </div>
      </section>

      <section className="w-full px-4 pb-12 md:px-8 md:pb-16">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-10">
          <div className="min-w-0">
            <div className="mb-5 flex flex-wrap items-center gap-1.5 rounded-lg border border-background-200 bg-background-50 p-1.5">
              {CATEGORIES.map((category) => (
                <button
                  key={category}
                  type="button"
                  onClick={() => setActive(category)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors ${
                    active === category
                      ? 'bg-primary-900 font-medium text-background-50'
                      : 'text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  {category}
                  {category !== '全部' && (
                    <span className="ml-1 text-[10px] opacity-70">
                      {newsArticles.filter((article) => article.category === category).length}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {paged.length > 0 ? (
              <ul className="space-y-4">
                {paged.map((article) => (
                  <li key={article.id}>
                    <article className="group grid grid-cols-1 gap-3 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-200 hover:bg-primary-50/40 sm:grid-cols-[200px_minmax(0,1fr)] sm:gap-4 md:p-4">
                      <Link
                        to={`/news/${article.id}`}
                        className="block w-full overflow-hidden rounded-md bg-background-200"
                      >
                        <div className="aspect-[16/9] w-full sm:aspect-[16/10]">
                          <img
                            src={article.cover}
                            alt={`${article.title} 配图`}
                            title={`${article.title} - 影视资讯 - 黄牛视频`}
                            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
                          />
                        </div>
                      </Link>
                      <div className="flex min-w-0 flex-col">
                        <div className="flex flex-wrap items-center gap-2 text-[11px] text-foreground-400">
                          <span className="rounded-sm bg-accent-100 px-1.5 py-0.5 font-medium text-accent-800">
                            {article.category}
                          </span>
                          <span>{article.publishedAt}</span>
                          <span className="hidden sm:inline">· {article.source}</span>
                        </div>
                        <h2 className="mt-2 font-heading text-base font-bold leading-snug text-foreground-950 md:text-lg">
                          <Link to={`/news/${article.id}`} className="hover:text-primary-700">
                            {article.title}
                          </Link>
                        </h2>
                        <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-500 md:text-[13px]">
                          {article.summary}
                        </p>
                        <Link
                          to={`/news/${article.id}`}
                          className="mt-3 flex items-center gap-1 text-xs font-medium text-primary-700 transition-colors hover:text-accent-700"
                        >
                          阅读全文
                          <i className="ri-arrow-right-line" />
                        </Link>
                      </div>
                    </article>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="rounded-lg border border-dashed border-background-300 bg-background-100/50 px-4 py-16 text-center">
                <i className="ri-newspaper-line text-2xl text-foreground-400" />
                <h2 className="mt-3 font-heading text-base font-bold text-foreground-900">
                  该分类暂无资讯
                </h2>
                <p className="mt-2 text-xs text-foreground-500">切换到其他分类，或查看全部报道。</p>
                <button
                  type="button"
                  onClick={() => setActive('全部')}
                  className="mt-5 cursor-pointer rounded-md bg-primary-900 px-5 py-2.5 text-sm font-medium text-background-50"
                >
                  查看全部资讯
                </button>
              </div>
            )}

            {totalPages > 1 && (
              <div className="mt-7 flex items-center justify-center gap-1.5">
                <button
                  type="button"
                  aria-label="上一页"
                  disabled={safePage === 1}
                  onClick={() => setPage(safePage - 1)}
                  className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <i className="ri-arrow-left-s-line" />
                </button>
                {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
                  <button
                    key={pageNumber}
                    type="button"
                    onClick={() => setPage(pageNumber)}
                    className={`h-8 min-w-8 cursor-pointer rounded-md px-2 font-label text-xs ${
                      pageNumber === safePage
                        ? 'bg-primary-900 font-semibold text-background-50'
                        : 'border border-background-300 text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    {pageNumber}
                  </button>
                ))}
                <button
                  type="button"
                  aria-label="下一页"
                  disabled={safePage === totalPages}
                  onClick={() => setPage(safePage + 1)}
                  className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <i className="ri-arrow-right-s-line" />
                </button>
              </div>
            )}
          </div>

          <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start">
            <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
              <div className="flex items-center justify-between bg-primary-900 px-4 py-3">
                <h2 className="font-heading text-sm font-bold text-background-50">热门阅读</h2>
                <span className="font-label text-[10px] uppercase tracking-wider text-primary-300">
                  Top 5
                </span>
              </div>
              <ol className="divide-y divide-background-200">
                {hot.map((article, index) => (
                  <li key={article.id}>
                    <Link
                      to={`/news/${article.id}`}
                      className="group flex gap-3 px-4 py-3 transition-colors hover:bg-background-100/70"
                    >
                      <span className="font-label text-sm font-bold text-accent-500">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <div className="min-w-0">
                        <p className="line-clamp-2 text-[13px] leading-relaxed text-foreground-800 group-hover:text-primary-700">
                          {article.title}
                        </p>
                        <p className="mt-1 text-[10px] text-foreground-400">{article.publishedAt}</p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ol>
            </div>

            <div className="rounded-lg bg-secondary-50 p-4">
              <p className="font-heading text-sm font-bold text-secondary-900">资讯说明</p>
              <p className="mt-2 text-[11px] leading-relaxed text-secondary-800/80">
                本站资讯为示例内容，用于展示频道结构与图文排版。资讯详情页不含任何外部跳转或商业推广。
              </p>
            </div>

            <div className="rounded-lg border border-background-200 p-4">
              <p className="font-heading text-sm font-bold text-foreground-950">继续浏览</p>
              <div className="mt-3 flex flex-col gap-2">
                {[
                  { label: '电影频道', to: '/category/movie', icon: 'ri-film-line' },
                  { label: '电视剧频道', to: '/category/tv', icon: 'ri-tv-2-line' },
                  { label: '综艺频道', to: '/category/variety', icon: 'ri-mic-line' },
                ].map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="flex items-center justify-between rounded-md bg-background-100 px-3 py-2 text-xs text-foreground-700 transition-colors hover:bg-background-200"
                  >
                    <span className="flex items-center gap-2">
                      <i className={`${item.icon} text-primary-700`} />
                      {item.label}
                    </span>
                    <i className="ri-arrow-right-s-line text-foreground-400" />
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>
    </SiteShell>
  );
}